<template>
  <div class="label" :class="computedLabelType" :aria-label="title">
    <Icon v-if="icon" :name="icon" class="label-icon" role="presentation" />
    <span class="label-title">{{ title }}</span>
  </div>
</template>

<script setup lang="ts">
import type { TLabel } from '~~/types/local_types';

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  type: {
    type: String as () => TLabel,
    default: 'text',
  },
  icon: {
    type: String,
    default: null,
  },
  variant: {
    type: String,
    default: undefined,
  },
});

const computedLabelType = computed(() => {
  const returnValue = [`is--${props.type}`];

  if (props.variant) {
    returnValue.push(`is--${props.variant}`);
  }

  return returnValue;
});
</script>

<style lang="postcss" scoped>
.label {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: var(--font-family-primary), sans-serif;

  /* TYPE */
  &.is--text {
    color: hsl(var(--color-secondary-500));
    @add-mixin heading-xs;
  }

  &.is--subtitle {
    color: hsl(var(--color-primary-700));
    @add-mixin heading-xs;
  }

  &.is--source {
    padding: var(--unit-2) var(--unit-6);
    border-radius: var(--unit-2);
    color: hsl(var(--color-neutral-white));
    background: hsla(var(--color-primary-950), 0.7);
    @add-mixin label-xs;
  }

  &.is--info {
    padding: var(--unit-4) var(--unit-6);
    border-radius: var(--unit-2);
    color: hsl(var(--color-neutral-white));
    background: hsl(var(--color-primary-700));
    @add-mixin heading-sm-caps;
  }

  &.is--icon {
    --label-icon-padding: var(--unit-6) var(--unit-8);
    --label-icon-gap: var(--unit-4);

    @add-mixin heading-sm;

    border-radius: var(--unit-4);
    display: flex;
    gap: var(--label-icon-gap);
    padding: var(--label-icon-padding);
    background: var(--effect-gradient-btn-primary);
    color: hsl(var(--color-neutral-white));
    box-shadow: var(--effect-box-shadow-primary);
    min-height: 33px;

    @media (--lg-viewport) {
      --label-icon-padding: var(--unit-8) var(--unit-12);
      --label-icon-gap: var(--unit-6);
    }
  }

  &.is--dark {
    color: hsl(var(--color-accent-bright-blue));
  }

  &.is--darker {
    color: hsl(var(--color-primary-700));
  }

  .label-icon {
    --icon-size: var(--unit-14);

    font-size: var(--icon-size);

    @media (--lg-viewport) {
      --icon-size: var(--unit-18);
    }
  }
}
</style>
